// ---------------------------------------------------------------------------
// Test Mode — simulated leads (15) + pipeline for Clean Code Culture SRL.
//
// A single default sales pipeline with stages New / Qualified / Proposal Sent /
// Negotiation / Won / Lost. The "Won" leads correspond to existing clients so
// the CRM stays coherent. Values range 5.000–80.000 EUR (stored in RON).
// ---------------------------------------------------------------------------

import type {
  Lead,
  LeadPipeline,
  LeadStage,
  LeadStageType,
  LeadBoardResult,
  LeadBoardColumn,
  LeadStats,
  LeadSource,
  LeadTemperature,
} from '../lib/leadsApi';
import { isoDate, isoDateTime, eur } from './company';
import { PEOPLE_REFS } from './people';
import { leadInteractionCounts } from './leadInteractions';

const PIPELINE_ID = 'pipe-sales';

interface StageSeed {
  id: string;
  name: string;
  color: string;
  type: LeadStageType;
}

const STAGE_SEEDS: StageSeed[] = [
  { id: 'stg-new', name: 'New', color: '#64748b', type: 'ACTIVE' },
  { id: 'stg-qualified', name: 'Qualified', color: '#3b82f6', type: 'ACTIVE' },
  { id: 'stg-proposal', name: 'Proposal Sent', color: '#8b5cf6', type: 'ACTIVE' },
  { id: 'stg-negotiation', name: 'Negotiation', color: '#f59e0b', type: 'ACTIVE' },
  { id: 'stg-won', name: 'Won', color: '#22c55e', type: 'WON' },
  { id: 'stg-lost', name: 'Lost', color: '#ef4444', type: 'LOST' },
];

const CREATED_AT = isoDateTime(-200, 10);
const UPDATED_AT = isoDateTime(-3, 12);

export function buildStages(): LeadStage[] {
  return STAGE_SEEDS.map((s, i) => ({
    id: s.id,
    pipelineId: PIPELINE_ID,
    name: s.name,
    color: s.color,
    order: i,
    type: s.type,
    createdAt: CREATED_AT,
  }));
}

export function buildPipeline(): LeadPipeline {
  return {
    id: PIPELINE_ID,
    name: 'Vânzări',
    description: 'Pipeline principal de vânzări pentru proiecte software.',
    isDefault: true,
    kind: 'SALES',
    stages: buildStages(),
    createdAt: CREATED_AT,
    updatedAt: UPDATED_AT,
  };
}

interface LeadSeed {
  id: string;
  title: string;
  company: string;
  contact: string;
  stageId: string;
  valueEur: number;
  source: LeadSource;
  temperature: LeadTemperature;
  assigneeId: string;
}

// 3 New, 4 Qualified, 3 Proposal Sent, 2 Negotiation, 2 Won, 1 Lost = 15.
const LEAD_SEEDS: LeadSeed[] = [
  {
    id: 'ld1',
    title: 'Portal intern HR',
    company: 'NextGen Media',
    contact: 'Irina Vlad',
    stageId: 'stg-new',
    valueEur: 12_000,
    source: 'WEBSITE',
    temperature: 'WARM',
    assigneeId: 'p16',
  },
  {
    id: 'ld2',
    title: 'App livrare mâncare',
    company: 'FoodFast SRL',
    contact: 'Bogdan Achim',
    stageId: 'stg-new',
    valueEur: 40_000,
    source: 'REFERRAL',
    temperature: 'HOT',
    assigneeId: 'p3',
  },
  {
    id: 'ld3',
    title: 'Website prezentare',
    company: 'GreenBuild',
    contact: 'Maria Cojocaru',
    stageId: 'stg-new',
    valueEur: 5_000,
    source: 'SOCIAL_MEDIA',
    temperature: 'COLD',
    assigneeId: 'p16',
  },
  {
    id: 'ld4',
    title: 'Platformă e-learning',
    company: 'EduPlatform SRL',
    contact: 'Simona Barbu',
    stageId: 'stg-qualified',
    valueEur: 55_000,
    source: 'EVENT',
    temperature: 'HOT',
    assigneeId: 'p1',
  },
  {
    id: 'ld5',
    title: 'Integrare ERP',
    company: 'IndustriaPlus',
    contact: 'Cornel Pavel',
    stageId: 'stg-qualified',
    valueEur: 48_000,
    source: 'REFERRAL',
    temperature: 'WARM',
    assigneeId: 'p4',
  },
  {
    id: 'ld6',
    title: 'Aplicație mobilă fitness',
    company: 'FitLife',
    contact: 'Alina Roșu',
    stageId: 'stg-qualified',
    valueEur: 30_000,
    source: 'WEBSITE',
    temperature: 'WARM',
    assigneeId: 'p3',
  },
  {
    id: 'ld7',
    title: 'Marketplace B2B',
    company: 'TradeHub',
    contact: 'Victor Enache',
    stageId: 'stg-qualified',
    valueEur: 80_000,
    source: 'EVENT',
    temperature: 'HOT',
    assigneeId: 'p1',
  },
  {
    id: 'ld8',
    title: 'Sistem ticketing',
    company: 'SupportCo',
    contact: 'Daniela Mureșan',
    stageId: 'stg-proposal',
    valueEur: 25_000,
    source: 'WEBSITE',
    temperature: 'WARM',
    assigneeId: 'p4',
  },
  {
    id: 'ld9',
    title: 'Dashboard BI',
    company: 'DataViz Corp SRL',
    contact: 'Laura Munteanu',
    stageId: 'stg-proposal',
    valueEur: 38_000,
    source: 'REFERRAL',
    temperature: 'HOT',
    assigneeId: 'p3',
  },
  {
    id: 'ld10',
    title: 'App rezervări',
    company: 'BookIt',
    contact: 'Rareș Tudor',
    stageId: 'stg-proposal',
    valueEur: 22_000,
    source: 'SOCIAL_MEDIA',
    temperature: 'WARM',
    assigneeId: 'p16',
  },
  {
    id: 'ld11',
    title: 'Migrare cloud',
    company: 'LogiTrans SA',
    contact: 'Dan Ardelean',
    stageId: 'stg-negotiation',
    valueEur: 60_000,
    source: 'REFERRAL',
    temperature: 'HOT',
    assigneeId: 'p2',
  },
  {
    id: 'ld12',
    title: 'Redesign UX',
    company: 'RetailHub SRL',
    contact: 'Oana Dragomir',
    stageId: 'stg-negotiation',
    valueEur: 18_000,
    source: 'WEBSITE',
    temperature: 'WARM',
    assigneeId: 'p6',
  },
  {
    id: 'ld13',
    title: 'HR Management System',
    company: 'PeopleFirst SRL',
    contact: 'Carmen Iliescu',
    stageId: 'stg-won',
    valueEur: 95_000,
    source: 'EVENT',
    temperature: 'HOT',
    assigneeId: 'p1',
  },
  {
    id: 'ld14',
    title: 'Analytics Dashboard',
    company: 'DataViz Corp SRL',
    contact: 'Laura Munteanu',
    stageId: 'stg-won',
    valueEur: 65_000,
    source: 'REFERRAL',
    temperature: 'HOT',
    assigneeId: 'p3',
  },
  {
    id: 'ld15',
    title: 'App internă',
    company: 'OldCorp',
    contact: 'Nicolae Dima',
    stageId: 'stg-lost',
    valueEur: 15_000,
    source: 'PHONE',
    temperature: 'COLD',
    assigneeId: 'p16',
  },
];

function stageRef(stageId: string) {
  const s = STAGE_SEEDS.find((x) => x.id === stageId);
  const order = STAGE_SEEDS.findIndex((x) => x.id === stageId);
  return s ? { id: s.id, name: s.name, color: s.color, type: s.type, order } : null;
}

function assigneeName(id: string): string {
  return PEOPLE_REFS.find((p) => p.id === id)?.name ?? 'Necunoscut';
}

export function buildLeads(): Lead[] {
  return LEAD_SEEDS.map((s, i) => {
    const stage = stageRef(s.stageId);
    // Coherent counts/last-contact derived from the lead's interaction data
    // (CLE-261) so the summary cards match the detail page exactly.
    const counts = leadInteractionCounts(s.id);
    return {
      id: s.id,
      identifier: `LEAD-${i + 1}`,
      title: s.title,
      description: `Lead simulat: ${s.title} pentru ${s.company}.`,
      pipelineId: PIPELINE_ID,
      pipeline: { id: PIPELINE_ID, name: 'Vânzări', kind: 'SALES' },
      stageId: s.stageId,
      stage,
      stageOrder: stage?.order ?? 0,
      contactName: s.contact,
      contactEmail: `${s.contact.toLowerCase().replace(/[^a-z]/g, '.')}@example.ro`,
      contactPhone: '+40 7XX XXX XXX',
      companyName: s.company,
      value: eur(s.valueEur),
      currency: 'RON',
      source: s.source,
      sourceLabel: null,
      temperature: s.temperature,
      assigneeId: s.assigneeId,
      assignee: { id: s.assigneeId, name: assigneeName(s.assigneeId) },
      creatorId: 'p1',
      creator: { id: 'p1', name: assigneeName('p1') },
      convertedToProjectId: null,
      convertedAt: s.stageId === 'stg-won' ? isoDateTime(-20, 11) : null,
      clientId: null,
      expectedCloseDate: isoDate(15 + i),
      lastContactedAt: counts.lastContactedAt ?? isoDateTime(-(i % 14), 10),
      labels: [],
      actionCount: counts.actionCount,
      commentCount: counts.commentCount,
      createdAt: isoDateTime(-(30 + i * 4), 9),
      updatedAt: UPDATED_AT,
      archivedAt: null,
    };
  });
}

export function buildLeadBoard(): LeadBoardResult {
  const leads = buildLeads();
  const stages = buildStages();
  const columns: LeadBoardColumn[] = stages.map((stage) => {
    const stageLeads = leads.filter((l) => l.stageId === stage.id);
    return {
      stage,
      leads: stageLeads,
      count: stageLeads.length,
      valueSum: stageLeads.reduce((sum, l) => sum + (l.value ?? 0), 0),
    };
  });
  return { pipeline: buildPipeline(), columns };
}

export function buildLeadStats(): LeadStats {
  const leads = buildLeads();
  const stages = buildStages();
  const won = leads.filter((l) => l.stage?.type === 'WON').length;
  const lost = leads.filter((l) => l.stage?.type === 'LOST').length;
  const closed = won + lost;
  return {
    totalLeads: leads.length,
    totalValue: leads.reduce((sum, l) => sum + (l.value ?? 0), 0),
    conversionRate: closed > 0 ? Math.round((won / closed) * 100) : 0,
    wonLeads: won,
    avgDaysInStage: 12,
    byStage: stages.map((stage) => {
      const stageLeads = leads.filter((l) => l.stageId === stage.id);
      return {
        stageId: stage.id,
        stageName: stage.name,
        stageType: stage.type,
        count: stageLeads.length,
        value: stageLeads.reduce((sum, l) => sum + (l.value ?? 0), 0),
      };
    }),
  };
}

export function buildLead(id: string): Lead | null {
  return buildLeads().find((l) => l.id === id) ?? null;
}
